/**
 * patrolService.js
 * Typed API adapter layer for all patrol-related endpoints.
 * Each function returns normalised data; never exposes raw axios response to consumers.
 */
import ApiCaller from '../api';

// ─────────────────────────────────────────────────────────
// Normalizers
// ─────────────────────────────────────────────────────────

/** Extracts rows + pagination from a Laravel paginator living in data.data */
export function normalizeLaravelPaginator(responseData) {
    const paginator = responseData?.data ?? {};
    return {
        rows: paginator.data ?? [],
        currentPage: paginator.current_page ?? 1,
        lastPage: paginator.last_page ?? 1,
        total: paginator.total ?? 0,
        perPage: paginator.per_page ?? 20,
        from: paginator.from ?? 0,
        to: paginator.to ?? 0,
    };
}

/** Normalises raw snake_case follow-up records to camelCase */
export function normalizeFollowUp(raw) {
    return {
        id: raw.id,
        guardCompanyId: raw.guard_company_id,
        propertyId: raw.property_id,
        checkpointId: raw.checkpoint_id,
        checkpointScanId: raw.checkpoint_scan_id,
        createdBy: raw.created_by,
        assignedTo: raw.assigned_to,
        acknowledgedBy: raw.acknowledged_by,
        resolvedBy: raw.resolved_by,
        status: raw.status,
        summary: raw.summary,
        details: raw.details,
        assignedAt: raw.assigned_at,
        acknowledgedAt: raw.acknowledged_at,
        resolvedAt: raw.resolved_at,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
        checkpoint: raw.checkpoint,
    };
}

/** Extracts the data payload from a common envelope response */
function unwrap(res) {
    return res?.data?.data ?? res?.data ?? null;
}

// ─────────────────────────────────────────────────────────
// Property / Checkpoint
// ─────────────────────────────────────────────────────────

export async function getPropertyDetail(propertyId) {
    const res = await ApiCaller.Get(`/property/${propertyId}`);
    return unwrap(res);
}

export async function createCheckpoint(propertyId, body) {
    const res = await ApiCaller.Post(`/property/${propertyId}/check-point`, body);
    // Response wraps full property in data
    return unwrap(res);
}

export async function updateCheckpoint(propertyId, checkpointId, body) {
    const res = await ApiCaller.Put(`/property/${propertyId}/check-point/${checkpointId}`, body);
    // Response is Property resource directly (no envelope)
    return res?.data?.data ?? res?.data ?? null;
}

export async function deleteCheckpoint(propertyId, checkpointId) {
    const res = await ApiCaller.Delete(`/property/${propertyId}/check-point/${checkpointId}`);
    return res?.data?.data ?? res?.data ?? null;
}

// ─────────────────────────────────────────────────────────
// QR Management
// ─────────────────────────────────────────────────────────

export async function getQrMeta(checkpointId) {
    const res = await ApiCaller.Get(`/checkpoints/${checkpointId}/qr`);
    return unwrap(res);
}

export async function downloadQrBlob(checkpointId, format = 'png') {
    // format: 'png' | 'pdf'
    const res = await ApiCaller.GetBlob(`/checkpoints/${checkpointId}/qr.${format}`);
    return res; // raw axios blob response
}

export async function rotateQr(checkpointId) {
    const res = await ApiCaller.Post(`/checkpoints/${checkpointId}/qr/rotate`, {});
    return unwrap(res);
}

// ─────────────────────────────────────────────────────────
// Checkpoint Transitions (Patrol Route)
// ─────────────────────────────────────────────────────────

export async function listTransitions(propertyId) {
    const res = await ApiCaller.Get(`/properties/${propertyId}/checkpoint-transitions`);
    return unwrap(res) ?? [];
}

export async function upsertTransition(propertyId, body) {
    const res = await ApiCaller.Post(`/properties/${propertyId}/checkpoint-transitions`, body);
    return unwrap(res);
}

export async function deleteTransition(propertyId, transitionId) {
    const res = await ApiCaller.Delete(`/properties/${propertyId}/checkpoint-transitions/${transitionId}`);
    return unwrap(res);
}

// ─────────────────────────────────────────────────────────
// Patrols
// ─────────────────────────────────────────────────────────

export async function listPatrols({ propertyId, guardId, status, perPage, page } = {}) {
    const params = {};
    if (propertyId) params.propertyId = propertyId;
    if (guardId) params.guardId = guardId;
    if (status) params.status = status;
    if (perPage) params.perPage = Math.min(100, Math.max(1, perPage));
    if (page) params.page = page;

    const res = await ApiCaller.Get('/patrols', params);
    // Paginator lives in data.data
    return normalizeLaravelPaginator(res?.data);
}

export async function getPatrolDetail(publicPatrolId) {
    const res = await ApiCaller.Get(`/patrols/${publicPatrolId}`);
    return unwrap(res);
}

// ─────────────────────────────────────────────────────────
// Follow-ups
// ─────────────────────────────────────────────────────────

export async function listFollowUps({ propertyId, status, perPage, page } = {}) {
    const params = {};
    if (propertyId) params.propertyId = propertyId;
    if (status) params.status = status;
    if (perPage) params.perPage = perPage;
    if (page) params.page = page;

    const res = await ApiCaller.Get('/checkpoint-follow-ups', params);
    const paginator = normalizeLaravelPaginator(res?.data);
    return {
        ...paginator,
        rows: paginator.rows.map(normalizeFollowUp),
    };
}

export async function updateFollowUp(followUpId, body) {
    const res = await ApiCaller.Patch(`/checkpoint-follow-ups/${followUpId}`, body);
    const raw = res?.data?.data ?? res?.data ?? null;
    return raw ? normalizeFollowUp(raw) : null;
}

// ─────────────────────────────────────────────────────────
// Property Notes
// ─────────────────────────────────────────────────────────

export async function listPropertyNotes(propertyId) {
    const res = await ApiCaller.Get(`/properties/${propertyId}/notes`);
    return unwrap(res) ?? [];
}

export async function createPermanentNote(propertyId, body) {
    const res = await ApiCaller.Post(`/properties/${propertyId}/permanent-notes`, body);
    return unwrap(res);
}

export async function archiveNote(propertyId, noteId) {
    const res = await ApiCaller.Delete(`/properties/${propertyId}/notes/${noteId}`);
    return unwrap(res);
}

// ─────────────────────────────────────────────────────────
// Contact Directory
// ─────────────────────────────────────────────────────────

export async function listDirectory() {
    const res = await ApiCaller.Get('/directory');
    return unwrap(res) ?? [];
}

export async function createContact(body) {
    const res = await ApiCaller.Post('/directory/contacts', body);
    return unwrap(res);
}

export async function updateContact(contactId, body) {
    const res = await ApiCaller.Put(`/directory/contacts/${contactId}`, body);
    return unwrap(res);
}

export async function archiveContact(contactId) {
    const res = await ApiCaller.Patch(`/directory/contacts/${contactId}/archive`, {});
    return unwrap(res);
}

export async function deleteContact(contactId) {
    const res = await ApiCaller.Delete(`/directory/contacts/${contactId}`);
    return unwrap(res);
}

// ─────────────────────────────────────────────────────────
// Property Phone Directory (global contact selection + site contacts)
// ─────────────────────────────────────────────────────────

/** { property, globalContacts, selectedGlobalContactIds, siteContacts } */
export async function getPropertyDirectory(propertyId) {
    const res = await ApiCaller.Get(`/properties/${propertyId}/directory`);
    return unwrap(res);
}

/** contactIds must be the complete list of checked global contact IDs. */
export async function updatePropertyGlobalContacts(propertyId, contactIds) {
    const res = await ApiCaller.Patch(`/properties/${propertyId}/directory/global-contacts`, { contactIds });
    return unwrap(res);
}

export async function createPropertyContact(propertyId, body) {
    const res = await ApiCaller.Post(`/properties/${propertyId}/directory/contacts`, body);
    return unwrap(res);
}

// ─────────────────────────────────────────────────────────
// Patrol Settings
// ─────────────────────────────────────────────────────────

export async function getCompanyPatrolSettings() {
    const res = await ApiCaller.Get('/guard-company/patrol-settings');
    return unwrap(res);
}

export async function updateCompanyPatrolSettings(body) {
    const res = await ApiCaller.Patch('/guard-company/patrol-settings', body);
    return unwrap(res);
}

export async function updatePropertyPatrolSettings(propertyId, body) {
    const res = await ApiCaller.Patch(`/properties/${propertyId}/patrol-settings`, body);
    return unwrap(res);
}
